import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CalendarClock, ShieldCheck, Swords, Plus, Check, Target, Handshake,
  FileText, Layers, ChevronDown, ChevronRight, Sparkles,
  DollarSign, Building2, X as XIcon, Search,
} from 'lucide-react';
import { loggedGet, loggedMutation, apiClient } from './loggedFetch';
import {
  currentFiscalYear,
  fiscalYearRange,
  formatFiscalYear,
  fiscalSpanLabel,
} from '@techstack/core';
import { useFiscalBasisContext } from '../../contexts/FiscalBasisContext';
import FiscalBasisToggle from '../FiscalBasisToggle/FiscalBasisToggle';
import PoLink from '../PoDetailModal/PoLink';
import StrategyBriefModal from './StrategyBriefModal';
import logger from '../../lib/logger';

// Days from today until the last day of a given fiscal year ON THE ACTIVE
// CALENDAR. Used to be pinned to `new Date(y, 5, 30)` — June 30 — which is
// simply the wrong horizon for a seller whose year ends July 31.
function daysUntilFyEnd(fy, cal, now = new Date()) {
  const [y, m, d] = fiscalYearRange(fy, cal).end.split('-').map(Number);
  const end = new Date(y, m - 1, d);
  end.setHours(23, 59, 59, 999);
  return Math.max(1, Math.ceil((end - now) / 86400000));
}

// Build window options dynamically. Includes 24-36 month presets so the
// 12-24 month CA-state procurement cycle is visible — for a contract
// ending in Mar 2027, sales engagement starts Mar 2026, so today (Apr
// 2026) we already need that contract on screen.
//
// The FY presets follow the VIEWER's fiscal calendar: "Rest of FY27" runs to
// Jun 30 on the state basis and to Jul 31 on the PANW basis, and each chip's
// tooltip spells out the span so the label is never ambiguous.
function buildWindowOptions(cal, now = new Date()) {
  const fy = currentFiscalYear(cal, now);
  const chip = (offset, prefix) => {
    const target = fy + offset;
    return {
      days: daysUntilFyEnd(target, cal, now),
      label: `${prefix} ${formatFiscalYear(target, cal)}`,
      title: `${cal.label} ${formatFiscalYear(target, cal)} (${fiscalSpanLabel(cal)}) — through ${fiscalYearRange(target, cal).end}`,
      kind: 'fy',
    };
  };
  return [
    { days: 90, label: '90 days', title: '90 days from today', kind: 'fixed' },
    chip(0, 'Rest of'),
    chip(1, 'Through'),
    chip(2, 'Through'),
    chip(3, 'Through'),
  ];
}

function formatCurrencyShort(n) {
  const v = Number(n) || 0;
  if (v >= 1e9) return `$${(v / 1e9).toFixed(2)}B`;
  if (v >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
  if (v >= 1e3) return `$${(v / 1e3).toFixed(0)}K`;
  return `$${Math.round(v)}`;
}

function formatDateShort(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

// Engage-by urgency. Now uses agency-specific budget_planning_lead_months
// (default 12) returned from the backend. "ENGAGE NOW" if you should already be
// in the conversation; "ENGAGE SOON" if you need to start within 90 days.
function engageBadge(daysToEngage) {
  if (daysToEngage == null) return null;
  if (daysToEngage <= 0) return { label: 'ENGAGE NOW', className: 'vca-renewal-now' };
  if (daysToEngage <= 90) return { label: 'ENGAGE SOON', className: 'vca-renewal-soon' };
  return { label: 'ON RADAR', className: 'vca-renewal-watch' };
}

// Format planning lead months for display
function formatLeadMonths(months) {
  if (!months || months === 12) return '12-month';
  return `${months}-month`;
}

// Group renewal rows into EA-candidate clusters: any (agency) for own
// renewals or (agency, competitor) for competitor renewals where 2+
// contracts surface in the window. Multiple contracts at the same
// account with the same vendor are by definition an EA-bundle play —
// the question is just whether the timing is tight (co-term, near-term
// EA conversation) or spread out (longer-horizon strategic relationship).
//
// Returns a list of items, each shaped:
//   { type: 'solo', row }
//   { type: 'cluster', agency, competitor?, members[], total_value,
//     earliest_end, latest_end, span_days, days_to_engage,
//     days_to_end, play_type? }
//
// Sorted by total/contract value desc so the biggest plays surface
// first regardless of clustering.
function clusterByEa(rows, kind) {
  const groups = new Map();
  for (const row of rows) {
    const key = kind === 'own'
      ? String(row.department_id ?? row.agency)
      : `${row.department_id ?? row.agency}::${row.competitor}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(row);
  }

  const items = [];
  for (const [, members] of groups) {
    if (members.length === 1) {
      items.push({ type: 'solo', row: members[0] });
      continue;
    }
    members.sort((a, b) => new Date(a.end_date) - new Date(b.end_date));
    const totalValue = members.reduce((s, m) => s + (Number(m.contract_value) || 0), 0);
    const earliestEnd = members[0].end_date;
    const latestEnd = members[members.length - 1].end_date;
    const spanDays = Math.round(
      (new Date(latestEnd) - new Date(earliestEnd)) / 86400000
    );
    // Earliest engage-by drives the cluster's urgency badge — if any
    // contract in the cluster needs engagement now, the cluster does.
    const earliestEngageRow = members.reduce(
      (m, r) => (r.days_to_engage < m.days_to_engage ? r : m),
      members[0]
    );
    items.push({
      type: 'cluster',
      agency: members[0].agency,
      department_id: members[0].department_id,
      competitor: kind === 'competitor' ? members[0].competitor : null,
      play_type: kind === 'competitor' ? members[0].play_type : 'defend',
      members,
      total_value: totalValue,
      earliest_end: earliestEnd,
      latest_end: latestEnd,
      span_days: spanDays,
      days_to_engage: earliestEngageRow.days_to_engage,
      days_to_end: members[0].days_to_end,
    });
  }

  // Order by combined value (cluster total or solo contract value).
  items.sort((a, b) => {
    const av = a.type === 'cluster' ? a.total_value : (a.row.contract_value || 0);
    const bv = b.type === 'cluster' ? b.total_value : (b.row.contract_value || 0);
    return bv - av;
  });
  return items;
}

function RenewalCalendar({ vendor, includeLowConfidence, strict, productFamily, departmentIds, listName, years, onDrilldown }) {
  const queryClient = useQueryClient();
  // The VIEWER's fiscal calendar drives the FY horizon chips.
  const { basis, calendar, isDefaultBasis } = useFiscalBasisContext();
  const windowOptions = React.useMemo(
    () => buildWindowOptions(calendar), [calendar],
  );
  // Default to "Through FY27" — gives ~14 months out, the start of the
  // typical CA budget-cycle planning window.
  const defaultPreset = windowOptions.find(o => o.kind === 'fy' && o.label.startsWith('Through')) || windowOptions[1];
  const [windowDays, setWindowDays] = useState(defaultPreset?.days ?? 540);

  // A basis flip moves every FY horizon by ~31 days. Re-pin the selection to
  // the equivalent chip on the new calendar instead of leaving a stale day
  // count that no longer lines up with any label.
  const prevBasisRef = useRef(basis);
  useEffect(() => {
    if (prevBasisRef.current === basis) return;
    const prevIndex = windowOptions.findIndex(o => o.days === windowDays);
    prevBasisRef.current = basis;
    const next = prevIndex >= 0 ? windowOptions[prevIndex] : defaultPreset;
    if (next) setWindowDays(next.days);
  }, [basis, windowOptions, windowDays, defaultPreset]);
  const [addedIds, setAddedIds] = useState({});
  const [playFilter, setPlayFilter] = useState('all'); // all | offensive | coexist
  // Phase 2 filters: contract size bucket + agency multi-select.
  const [sizeFilter, setSizeFilter] = useState('all'); // all | lt100k | 100k1m | 1m5m | gt5m
  const [agencyFilter, setAgencyFilter] = useState(() => new Set()); // empty = all agencies
  const [agencyMenuOpen, setAgencyMenuOpen] = useState(false);
  const [agencyQuery, setAgencyQuery] = useState('');
  const agencyMenuRef = useRef(null);
  const [expandedClusters, setExpandedClusters] = useState({});
  // Opportunity object currently being explained by Claude. null = closed.
  const [briefOpportunity, setBriefOpportunity] = useState(null);
  // Mobile tab: 'own' or 'competitor'
  const [mobileTab, setMobileTab] = useState('own');

  // Close the agency dropdown when clicking outside it.
  useEffect(() => {
    if (!agencyMenuOpen) return;
    const onClick = (e) => {
      if (agencyMenuRef.current && !agencyMenuRef.current.contains(e.target)) {
        setAgencyMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [agencyMenuOpen]);

  const qs = new URLSearchParams();
  qs.set('windowDays', String(windowDays));
  if (includeLowConfidence) qs.set('includeLowConfidence', 'true');
  if (!strict) qs.set('strict', 'false');
  // Forward-compat: /renewals doesn't filter by product_family yet (the
  // mv_renewals matview lacks component_id at the grain we'd need). Sending
  // the param now so when the matview is rebuilt the chip starts working
  // without a frontend change.
  if (productFamily) qs.set('product_family', productFamily);
  // Phase H.29: department_ids filter for list scoping
  if (departmentIds?.length) qs.set('department_ids', departmentIds.join(','));
  // Global time filter from TimeFilterContext (null = all-time)
  if (years != null) qs.set('years', String(years));
  // Omit on the default basis so the request is byte-identical to what it was
  // before the toggle existed.
  if (!isDefaultBasis) qs.set('basis', basis);
  const path = `/api/vendor-analysis/${encodeURIComponent(vendor || '')}/renewals?${qs}`;

  const { data, isLoading, error } = useQuery({
    queryKey: ['vendor-analysis', 'renewals', vendor, windowDays, includeLowConfidence, strict, productFamily, departmentIds, years, basis],
    queryFn: loggedGet('RenewalCalendar:fetch', path),
    enabled: !!vendor,
  });

  const addCard = useMutation({
    mutationFn: loggedMutation(
      'RenewalCalendar:add-to-plan',
      (body) => apiClient.post(
        `/api/vendor-analysis/${encodeURIComponent(vendor)}/plan/cards`, body,
      ),
    ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['vendor-analysis', 'plan', vendor] }),
  });

  const seedFromRow = (row, kind) => {
    const isOwn = kind === 'own';
    const competitor = row.competitor || row.supplier_name;
    const days = row.days_to_engage ?? row.days_to_end ?? 0;
    const colKey = days <= 30 ? '30' : days <= 60 ? '60' : days <= 90 ? '90' : 'backlog';

    const playLabel = isOwn
      ? 'Defend / expand'
      : (row.play_type === 'offensive' ? 'Offensive displace' : 'Coexist / EA wrap');

    const title = isOwn
      ? `Renew ${vendor} at ${row.agency} — ${row.top_component || row.category || 'contract'}`
      : `${row.play_type === 'offensive' ? 'Displace' : 'Wrap'} ${competitor} at ${row.agency} — ${row.top_component || row.category}`;

    const rationale = [
      isOwn
        ? `${vendor} contract at ${row.agency} (${formatCurrencyShort(row.contract_value)}, ${row.line_count} line${row.line_count === 1 ? '' : 's'}) ends ${formatDateShort(row.end_date)}.`
        : `${competitor} contract at ${row.agency} (${formatCurrencyShort(row.contract_value)}, ${row.line_count} line${row.line_count === 1 ? '' : 's'}) ends ${formatDateShort(row.end_date)}.`,
      `Engage by: ${row.days_to_engage <= 0 ? 'ASAP — already inside budget cycle' : `${row.days_to_engage} days (~${formatDateShort(addDays(new Date(), row.days_to_engage))})`}.`,
      `PO ${row.purchase_document_num}.`,
      `Play: ${playLabel}.`,
      isOwn ? '' : (row.play_type === 'offensive'
        ? `${vendor} has no recorded presence at ${row.agency} — unsolicited proposal play.`
        : `${vendor} already has presence at ${row.agency} — bundle into the existing EA conversation.`),
      row.acquisition_method ? `Procurement vehicle: ${row.acquisition_method}.` : '',
      row.lpa_contract_id ? `LPA: ${row.lpa_contract_id}.` : '',
    ].filter(Boolean).join(' ');

    addCard.mutate({
      column_key: colKey,
      title,
      rationale,
      dollar_impact: row.contract_value,
      due_date: row.end_date,
      seed_kind: isOwn ? 'renewal_defend' : (row.play_type === 'offensive' ? 'renewal_offensive' : 'renewal_coexist'),
      seed_payload: {
        purchase_document_num: row.purchase_document_num,
        department_id: row.department_id,
        agency: row.agency,
        category: row.category,
        end_date: row.end_date,
        engage_by_days: row.days_to_engage,
        competitor: isOwn ? null : competitor,
        play_type: isOwn ? 'defend' : row.play_type,
        contract_value: row.contract_value,
        line_count: row.line_count,
      },
    }, {
      onSuccess: () => setAddedIds(prev => ({ ...prev, [`${kind}-${row.purchase_document_num}-${row.department_id}`]: true })),
    });
  };

  // Seed ONE consolidated card from an EA-candidate cluster instead of N
  // individual cards per member PO. Captures the multi-contract aggregate
  // ($X total across N POs ending over Y days) and frames the action as
  // "wrap into EA" rather than per-PO renewal.
  const seedClusterToPlan = (cluster, kind) => {
    const isOwn = kind === 'own';
    const days = cluster.days_to_engage ?? cluster.days_to_end ?? 0;
    const colKey = days <= 30 ? '30' : days <= 60 ? '60' : days <= 90 ? '90' : 'backlog';
    const competitorLabel = isOwn ? null : cluster.competitor;
    const motion = isOwn
      ? `Consolidate ${vendor} EA renewal at ${cluster.agency}`
      : (cluster.play_type === 'offensive'
          ? `Displace ${competitorLabel} bundle at ${cluster.agency} (${cluster.members.length} contracts)`
          : `Wrap ${competitorLabel} contracts at ${cluster.agency} into ${vendor} EA`);

    const rationale = [
      `${cluster.agency} has ${cluster.members.length} ${isOwn ? vendor : competitorLabel} contracts ending over a ${cluster.span_days}-day window (${formatDateShort(cluster.earliest_end)} – ${formatDateShort(cluster.latest_end)}).`,
      `Combined contract value: ${formatCurrencyShort(cluster.total_value)}.`,
      `Earliest engage-by: ${cluster.days_to_engage <= 0 ? 'ASAP — already inside budget cycle' : `${cluster.days_to_engage} days`}.`,
      isOwn
        ? 'Multi-contract account = clear EA renewal/consolidation conversation.'
        : (cluster.play_type === 'offensive'
            ? `${vendor} has zero presence here — propose one EA covering all ${cluster.members.length} contracts.`
            : `${vendor} already present — bundle these ${cluster.members.length} contracts into the existing EA at the next renewal.`),
      `POs: ${cluster.members.map(m => m.purchase_document_num).join(', ')}.`,
    ].filter(Boolean).join(' ');

    addCard.mutate({
      column_key: colKey,
      title: motion,
      rationale,
      dollar_impact: cluster.total_value,
      due_date: cluster.earliest_end,
      seed_kind: isOwn ? 'ea_consolidate' : (cluster.play_type === 'offensive' ? 'ea_offensive_bundle' : 'ea_wrap'),
      seed_payload: {
        agency: cluster.agency,
        department_id: cluster.department_id,
        competitor: competitorLabel,
        play_type: isOwn ? 'defend' : cluster.play_type,
        member_count: cluster.members.length,
        member_pos: cluster.members.map(m => m.purchase_document_num),
        total_value: cluster.total_value,
        earliest_end: cluster.earliest_end,
        latest_end: cluster.latest_end,
        span_days: cluster.span_days,
        days_to_engage: cluster.days_to_engage,
      },
    }, {
      onSuccess: () => setAddedIds(prev => ({
        ...prev,
        [`cluster-${kind}-${cluster.department_id}-${competitorLabel || vendor}`]: true,
      })),
    });
  };

  // Raw row lists from the API. Defaulted so the memos below can run
  // before data arrives — Rules of Hooks require these to be called on
  // every render, including the loading/error renders.
  const rawOwn = data?.own || [];
  const rawCompetitor = data?.competitor || [];

  // Per-bucket counts for the size-filter chips (computed before size+agency
  // filtering so the user can see what each chip would surface).
  const sizeCounts = useMemo(() => {
    const all = [...rawOwn, ...rawCompetitor].filter(
      (r) => agencyFilter.size === 0 || agencyFilter.has(r.agency)
    );
    const buckets = { all: all.length, lt100k: 0, '100k1m': 0, '1m5m': 0, gt5m: 0 };
    for (const r of all) {
      const v = r.contract_value || 0;
      if (v < 100_000) buckets.lt100k += 1;
      else if (v < 1_000_000) buckets['100k1m'] += 1;
      else if (v < 5_000_000) buckets['1m5m'] += 1;
      else buckets.gt5m += 1;
    }
    return buckets;
  // eslint-disable-next-line
  }, [rawOwn, rawCompetitor, agencyFilter]);

  // Distinct agencies present in the current windowed data, sorted by
  // total contract value desc so the most "loaded" agencies surface
  // first in the picker.
  const agencyChoices = useMemo(() => {
    const tally = new Map();
    for (const r of [...rawOwn, ...rawCompetitor]) {
      if (!r.agency) continue;
      const cur = tally.get(r.agency) || { agency: r.agency, count: 0, value: 0 };
      cur.count += 1;
      cur.value += r.contract_value || 0;
      tally.set(r.agency, cur);
    }
    return Array.from(tally.values()).sort((a, b) => b.value - a.value);
  }, [rawOwn, rawCompetitor]);

  const filteredAgencyChoices = useMemo(() => {
    if (!agencyQuery.trim()) return agencyChoices;
    const q = agencyQuery.toLowerCase();
    return agencyChoices.filter(c => c.agency.toLowerCase().includes(q));
  }, [agencyChoices, agencyQuery]);

  if (isLoading) return <div className="vca-section vca-section__loading">Loading renewal calendar…</div>;
  if (error) return <div className="vca-section vca-section__error">Renewals failed: {error.message}</div>;

  // Apply Phase 2 filters (size + agency) on top of the existing
  // play-type filter.
  const matchesSize = (row) => {
    const v = row.contract_value || 0;
    if (sizeFilter === 'all') return true;
    if (sizeFilter === 'lt100k') return v < 100_000;
    if (sizeFilter === '100k1m') return v >= 100_000 && v < 1_000_000;
    if (sizeFilter === '1m5m') return v >= 1_000_000 && v < 5_000_000;
    if (sizeFilter === 'gt5m') return v >= 5_000_000;
    return true;
  };

  const matchesAgency = (row) => agencyFilter.size === 0 || agencyFilter.has(row.agency);

  const filterRows = (rows) =>
    rows.filter(matchesSize).filter(matchesAgency);

  const own = filterRows(rawOwn);
  let competitor = filterRows(rawCompetitor);
  if (playFilter !== 'all') {
    competitor = competitor.filter(r => r.play_type === playFilter);
  }
  const ownTotal = own.reduce((s, r) => s + (r.contract_value || 0), 0);
  const compTotal = competitor.reduce((s, r) => s + (r.contract_value || 0), 0);
  const offensiveCount = (data?.competitor || []).filter(r => r.play_type === 'offensive').length;
  const coexistCount = (data?.competitor || []).filter(r => r.play_type === 'coexist').length;

  const toggleAgency = (acronym) => {
    setAgencyFilter(prev => {
      const next = new Set(prev);
      if (next.has(acronym)) next.delete(acronym);
      else next.add(acronym);
      return next;
    });
  };

  const filtersActive =
    sizeFilter !== 'all' || agencyFilter.size > 0 || playFilter !== 'all';

  const clearAllFilters = () => {
    setSizeFilter('all');
    setAgencyFilter(new Set());
    setPlayFilter('all');
  };

  // Group both lists into EA-candidate clusters + solos.
  const ownClustered = clusterByEa(own, 'own');
  const competitorClustered = clusterByEa(competitor, 'competitor');
  const ownClusterCount = ownClustered.filter(i => i.type === 'cluster').length;
  const competitorClusterCount = competitorClustered.filter(i => i.type === 'cluster').length;

  const renderRow = (row, kind) => {
    const rowKey = `${kind}-${row.purchase_document_num}-${row.department_id}`;
    const added = !!addedIds[rowKey];
    const eng = engageBadge(row.days_to_engage);
    const drillTarget = kind === 'own' ? vendor : (row.competitor || row.supplier_name);
    const compsTooltip = (row.components || []).join('\n');
    return (
      <tr
        key={rowKey}
        className="clickable"
        onClick={() => {
          logger.vendorAnalysis.info('RenewalCalendar:row-click', { kind, vendor: drillTarget, agency: row.agency, po: row.purchase_document_num });
          onDrilldown({
            type: 'vendor',
            value: drillTarget,
            department: row.agency,
            departmentId: row.department_id,
          });
        }}
      >
        <td>
          {eng && (
            <div>
              <span className={`vca-renewal-badge ${eng.className}`}>{eng.label}</span>
              <div
                className="vca-section__sub"
                style={{ fontSize: '0.7rem', marginTop: 2 }}
                title={`Forecast: Engage-by is computed as contract end date minus ${formatLeadMonths(row.budget_planning_lead_months)} planning cycle${row.budget_planning_lead_months !== 12 ? ` (${row.agency} uses ${row.budget_planning_lead_months}-month cycles)` : ''}`}
              >
                <span className="vca-computed-value" style={{ textDecoration: 'none', fontStyle: 'normal' }}>
                  {row.days_to_engage <= 0 ? 'now' : `${row.days_to_engage}d`}
                </span>
                {' '}· ends {row.days_to_end}d
                {row.budget_planning_lead_months && row.budget_planning_lead_months !== 12 && (
                  <span className="vca-planning-cycle-badge" title={`${row.agency} uses ${row.budget_planning_lead_months}-month planning cycle`}>
                    {row.budget_planning_lead_months}mo
                  </span>
                )}
              </div>
            </div>
          )}
        </td>
        <td>
          <strong>{row.agency}</strong>
          {kind === 'competitor' && (
            <div className="vca-section__sub" style={{ fontSize: '0.72rem' }}>
              {row.play_type === 'offensive' ? (
                <span style={{ color: 'var(--color-warning)', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                  <Target size={11} /> Offensive · {vendor} absent
                </span>
              ) : (
                <span style={{ color: 'var(--color-info)', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                  <Handshake size={11} /> Coexist · wrap into EA
                </span>
              )}
            </div>
          )}
        </td>
        {kind === 'competitor' && <td>{row.competitor}</td>}
        <td>
          <div title={compsTooltip}>
            {row.top_component || row.category || '—'}
          </div>
          {row.line_count > 1 && (
            <div className="vca-section__sub" style={{ fontSize: '0.7rem' }}>
              + {row.line_count - 1} other line{row.line_count - 1 === 1 ? '' : 's'} on PO
            </div>
          )}
        </td>
        <td>
          <PoLink documentNumber={row.purchase_document_num} departmentId={row.department_id} startDate={row.earliest_start_date} />
          {row.acquisition_method && (
            <div className="vca-section__sub" style={{ fontSize: '0.7rem' }} title={row.acquisition_method}>
              <FileText size={9} style={{ verticalAlign: -1 }} /> {truncate(row.acquisition_method, 24)}
            </div>
          )}
        </td>
        <td>{formatDateShort(row.end_date)}</td>
        <td className="vca-num">
          <strong>{formatCurrencyShort(row.contract_value)}</strong>
          <div className="vca-section__sub" style={{ fontSize: '0.7rem' }}>
            {row.line_count} line{row.line_count === 1 ? '' : 's'}
          </div>
        </td>
        <td>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {kind === 'competitor' && (
              <button
                className="vca-add-btn"
                style={{
                  background: 'transparent',
                  color: 'var(--color-accent)',
                  border: '1px solid color-mix(in srgb, var(--color-accent) 35%, var(--color-border))',
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  logger.vendorAnalysis.info('RenewalCalendar:why-click', {
                    vendor, competitor: row.competitor, agency: row.agency, po: row.purchase_document_num,
                  });
                  setBriefOpportunity(row);
                }}
                title="Generate AI strategy brief for this opportunity"
              >
                <Sparkles size={12} /> Why?
              </button>
            )}
            <button
              className="vca-add-btn"
              disabled={added || addCard.isPending}
              onClick={(e) => { e.stopPropagation(); seedFromRow(row, kind); }}
              title="Add to plan with engage_by date as due"
            >
              {added ? <Check size={14} /> : <Plus size={14} />} {added ? 'Added' : 'Add to plan'}
            </button>
          </div>
        </td>
      </tr>
    );
  };

  // Render an EA-candidate cluster: header row with combined ARR +
  // engage timing + add-cluster button, then expanded member rows when
  // the user clicks the chevron.
  const renderCluster = (cluster, kind) => {
    const clusterKey = `cluster-${kind}-${cluster.department_id}-${cluster.competitor || vendor}`;
    const expanded = !!expandedClusters[clusterKey];
    const added = !!addedIds[clusterKey];
    const eng = engageBadge(cluster.days_to_engage);
    const colSpan = kind === 'competitor' ? 8 : 7;

    return (
      <React.Fragment key={clusterKey}>
        <tr
          className="vca-renewal-cluster"
          onClick={() => setExpandedClusters(p => ({ ...p, [clusterKey]: !p[clusterKey] }))}
        >
          <td colSpan={colSpan}>
            <div className="vca-renewal-cluster__head">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                <span className="vca-ea-badge">
                  <Layers size={11} /> EA candidate
                </span>
                {eng && (
                  <span className={`vca-renewal-badge ${eng.className}`}>{eng.label}</span>
                )}
                <strong>{cluster.agency}</strong>
                {kind === 'competitor' && (
                  <span style={{ color: 'var(--color-text-muted)' }}>×</span>
                )}
                {kind === 'competitor' && <strong>{cluster.competitor}</strong>}
                <span className="vca-section__sub">
                  · {cluster.members.length} contracts spanning {cluster.span_days}d
                  ({formatDateShort(cluster.earliest_end)} – {formatDateShort(cluster.latest_end)})
                </span>
                {kind === 'competitor' && (
                  cluster.play_type === 'offensive' ? (
                    <span style={{ color: 'var(--color-warning)', display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: '0.8rem' }}>
                      <Target size={11} /> Offensive bundle
                    </span>
                  ) : (
                    <span style={{ color: 'var(--color-info)', display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: '0.8rem' }}>
                      <Handshake size={11} /> Wrap into EA
                    </span>
                  )
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <strong style={{ fontSize: '0.95rem' }}>
                  {formatCurrencyShort(cluster.total_value)}
                </strong>
                <button
                  className="vca-add-btn"
                  disabled={added || addCard.isPending}
                  onClick={(e) => { e.stopPropagation(); seedClusterToPlan(cluster, kind); }}
                  title={`Seed one consolidated EA-bundle card covering all ${cluster.members.length} contracts`}
                >
                  {added ? <Check size={14} /> : <Plus size={14} />}
                  {added ? 'Added' : 'Add bundle'}
                </button>
              </div>
            </div>
          </td>
        </tr>
        {expanded && cluster.members.map(member => renderRow(member, kind))}
      </React.Fragment>
    );
  };

  const renderItems = (items, kind) =>
    items.map(item =>
      item.type === 'cluster' ? renderCluster(item, kind) : renderRow(item.row, kind)
    );

  return (
    <div className="vca-section">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <div>
          <h2><CalendarClock size={18} style={{ verticalAlign: -3, marginRight: 6 }} />Renewal calendar</h2>
          <p className="vca-section__sub">
            Contract-level view (one row per PO, not per line). Anchored on
            <strong> engage-by date </strong>(end date − agency planning lead time).
            Most agencies use 12-month cycles; CDT uses 18mo, large agencies (CDCR, DHCS) use 15mo.
            "Add to plan" auto-buckets by how-soon-to-engage.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {windowOptions.map(opt => (
            <button
              key={opt.label}
              type="button"
              className="vca-add-btn"
              onClick={() => setWindowDays(opt.days)}
              style={{
                background: windowDays === opt.days ? 'var(--color-accent)' : 'transparent',
                color: windowDays === opt.days ? 'var(--color-text-inverse)' : 'var(--color-text-muted)',
                border: '1px solid var(--color-border)',
              }}
              title={opt.title || `${opt.days} days from today`}
            >
              {opt.label}
            </button>
          ))}
          <FiscalBasisToggle surface="renewals" />
        </div>
      </div>

      <div className="vca-renewal-filters">
        <div className="vca-renewal-filter-group">
          <span className="vca-renewal-filter-label">
            <DollarSign size={11} /> Size
          </span>
          <SizeChip current={sizeFilter} value="all" count={sizeCounts.all} onClick={setSizeFilter}>
            All
          </SizeChip>
          <SizeChip current={sizeFilter} value="lt100k" count={sizeCounts.lt100k} onClick={setSizeFilter}>
            &lt;$100K
          </SizeChip>
          <SizeChip current={sizeFilter} value="100k1m" count={sizeCounts['100k1m']} onClick={setSizeFilter}>
            $100K–$1M
          </SizeChip>
          <SizeChip current={sizeFilter} value="1m5m" count={sizeCounts['1m5m']} onClick={setSizeFilter}>
            $1M–$5M
          </SizeChip>
          <SizeChip current={sizeFilter} value="gt5m" count={sizeCounts.gt5m} onClick={setSizeFilter}>
            $5M+
          </SizeChip>
        </div>

        <div className="vca-renewal-filter-group" ref={agencyMenuRef}>
          <button
            type="button"
            className={`vca-renewal-agency-trigger ${agencyFilter.size > 0 ? 'is-active' : ''}`}
            onClick={() => setAgencyMenuOpen(o => !o)}
          >
            <Building2 size={11} />
            <span>
              {agencyFilter.size === 0
                ? 'All agencies'
                : `${agencyFilter.size} agenc${agencyFilter.size === 1 ? 'y' : 'ies'}`}
            </span>
            <ChevronDown size={11} />
          </button>
          {agencyMenuOpen && (
            <div className="vca-renewal-agency-menu" role="listbox">
              <div className="vca-renewal-agency-search">
                <Search size={12} />
                <input
                  autoFocus
                  type="text"
                  value={agencyQuery}
                  onChange={(e) => setAgencyQuery(e.target.value)}
                  placeholder="Filter agencies…"
                />
                {agencyFilter.size > 0 && (
                  <button
                    type="button"
                    className="vca-renewal-agency-clear"
                    onClick={() => setAgencyFilter(new Set())}
                    title="Clear all selected agencies"
                  >
                    Clear
                  </button>
                )}
              </div>
              <div className="vca-renewal-agency-list">
                {filteredAgencyChoices.length === 0 && (
                  <div className="vca-renewal-agency-empty">No agencies match.</div>
                )}
                {filteredAgencyChoices.map(c => {
                  const checked = agencyFilter.has(c.agency);
                  return (
                    <label key={c.agency} className={`vca-renewal-agency-row ${checked ? 'is-checked' : ''}`}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleAgency(c.agency)}
                      />
                      <span className="vca-renewal-agency-name">{c.agency}</span>
                      <span className="vca-renewal-agency-meta">
                        {c.count} · {formatCurrencyShort(c.value)}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {filtersActive && (
          <button
            type="button"
            className="vca-renewal-filter-clear"
            onClick={clearAllFilters}
            title="Clear all filters and show every renewal in the window"
          >
            <XIcon size={11} /> Clear filters
          </button>
        )}
      </div>

      {/* Mobile tabs — only visible below 1024px */}
      <div className="vca-renewal-tabs">
        <button
          type="button"
          className={`vca-renewal-tab ${mobileTab === 'own' ? 'is-active' : ''}`}
          onClick={() => setMobileTab('own')}
        >
          <ShieldCheck size={16} />
          {vendor} Renewals
          <span className="vca-renewal-tab-count">{own.length}</span>
        </button>
        <button
          type="button"
          className={`vca-renewal-tab ${mobileTab === 'competitor' ? 'is-active' : ''}`}
          onClick={() => setMobileTab('competitor')}
        >
          <Swords size={16} />
          Competitor
          <span className="vca-renewal-tab-count">{competitor.length}</span>
        </button>
      </div>

      <div className="vca-renewal-grid">
        {/* Own renewals panel */}
        <div className={`vca-renewal-panel vca-renewal-panel--own ${mobileTab !== 'own' ? 'is-hidden' : ''}`}>
          <div className="vca-renewal-panel__header">
            <div className="vca-renewal-col-head" style={{ margin: 0, padding: 0, border: 'none' }}>
              <ShieldCheck size={16} /> {vendor} renewals · {own.length} contracts · {formatCurrencyShort(ownTotal)} at risk
              {ownClusterCount > 0 && (
                <span className="vca-section__sub" style={{ marginLeft: 6 }}>
                  · {ownClusterCount} EA candidate{ownClusterCount === 1 ? '' : 's'}
                </span>
              )}
            </div>
          </div>
          <div className="vca-renewal-panel__content">
            {own.length === 0 ? (
              <div className="vca-section__sub">No {vendor} contracts ending in this window.</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
              <table className="vca-table">
                <thead>
                  <tr>
                    <th>Engage</th><th>Agency</th><th>Product</th><th>PO</th><th>Ends</th><th className="vca-num">Contract value</th><th></th>
                  </tr>
                </thead>
                <tbody>{renderItems(ownClustered, 'own')}</tbody>
              </table>
              </div>
            )}
          </div>
        </div>

        {/* Competitor renewals panel */}
        <div className={`vca-renewal-panel vca-renewal-panel--competitor ${mobileTab !== 'competitor' ? 'is-hidden' : ''}`}>
          <div className="vca-renewal-panel__header">
            <div className="vca-renewal-col-head" style={{ margin: 0, padding: 0, border: 'none', flex: '1 1 auto' }}>
              <Swords size={16} /> Competitor renewals · {competitor.length} contracts · {formatCurrencyShort(compTotal)} addressable
              {competitorClusterCount > 0 && (
                <span className="vca-section__sub" style={{ marginLeft: 6 }}>
                  · {competitorClusterCount} EA candidate{competitorClusterCount === 1 ? '' : 's'}
                </span>
              )}
            </div>
            <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
              <PlayChip current={playFilter} value="all" onClick={setPlayFilter}>
                All ({(data?.competitor || []).length})
              </PlayChip>
              <PlayChip current={playFilter} value="offensive" onClick={setPlayFilter}>
                <Target size={11} /> Offensive ({offensiveCount})
              </PlayChip>
              <PlayChip current={playFilter} value="coexist" onClick={setPlayFilter}>
                <Handshake size={11} /> Coexist ({coexistCount})
              </PlayChip>
            </div>
          </div>
          <div className="vca-renewal-panel__content">
            {competitor.length === 0 ? (
              <div className="vca-section__sub">
                {playFilter === 'all' ? `No competitor contracts ending in this window.` : `No competitor contracts match the ${playFilter} filter.`}
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
              <table className="vca-table">
                <thead>
                  <tr>
                    <th>Engage</th><th>Agency</th><th>Competitor</th><th>Product</th><th>PO</th><th>Ends</th><th className="vca-num">Contract value</th><th></th>
                  </tr>
                </thead>
                <tbody>{renderItems(competitorClustered, 'competitor')}</tbody>
              </table>
              </div>
            )}
          </div>
        </div>
      </div>

      <StrategyBriefModal
        vendor={vendor}
        opportunity={briefOpportunity}
        isOpen={!!briefOpportunity}
        onClose={() => setBriefOpportunity(null)}
        onAddToPlan={(enrichedRow) => {
          // The modal hands back the original row plus an `ai_brief` and a
          // `rationale_override` string. Seed the kanban card with that
          // rationale rather than the templated one so the AI reasoning
          // persists with the play.
          const days = enrichedRow.days_to_engage ?? enrichedRow.days_to_end ?? 0;
          const colKey = days <= 30 ? '30' : days <= 60 ? '60' : days <= 90 ? '90' : 'backlog';
          addCard.mutate({
            column_key: colKey,
            title: `${enrichedRow.play_type === 'offensive' ? 'Displace' : 'Wrap'} ${enrichedRow.competitor} at ${enrichedRow.agency} — ${enrichedRow.top_component || enrichedRow.category || ''}`.trim(),
            rationale: enrichedRow.rationale_override,
            dollar_impact: enrichedRow.contract_value,
            due_date: enrichedRow.end_date,
            seed_kind: enrichedRow.play_type === 'offensive' ? 'renewal_offensive_ai' : 'renewal_coexist_ai',
            seed_payload: {
              purchase_document_num: enrichedRow.purchase_document_num,
              department_id: enrichedRow.department_id,
              agency: enrichedRow.agency,
              competitor: enrichedRow.competitor,
              competitor_product: enrichedRow.competitor_product,
              play_type: enrichedRow.play_type,
              ai_brief: enrichedRow.ai_brief,
              contract_value: enrichedRow.contract_value,
              line_count: enrichedRow.line_count,
            },
          }, {
            onSuccess: () => setAddedIds(prev => ({
              ...prev,
              [`competitor-${enrichedRow.purchase_document_num}-${enrichedRow.department_id}`]: true,
            })),
          });
        }}
      />
    </div>
  );
}

function SizeChip({ current, value, count, onClick, children }) {
  const active = current === value;
  return (
    <button
      type="button"
      className={`vca-renewal-size-chip ${active ? 'is-active' : ''}`}
      onClick={() => onClick(value)}
      title={`${count} contract${count === 1 ? '' : 's'} in this size band`}
    >
      <span>{children}</span>
      <span className="vca-renewal-size-chip-count">{count}</span>
    </button>
  );
}

function PlayChip({ current, value, onClick, children }) {
  const active = current === value;
  return (
    <button
      type="button"
      onClick={() => onClick(value)}
      style={{
        background: active ? 'var(--color-accent)' : 'transparent',
        color: active ? 'var(--color-text-inverse)' : 'var(--color-text-muted)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius-full)',
        padding: '2px 8px',
        cursor: 'pointer',
        fontSize: '0.72rem',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 3,
      }}
    >
      {children}
    </button>
  );
}

function truncate(s, n) {
  if (!s) return '';
  return s.length > n ? `${s.slice(0, n)}…` : s;
}

function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

export default RenewalCalendar;
